import { platformApiClient } from './platform-api-client';
import type {
  PlatformPlan,
  PlatformTenantDetail,
  PlatformTenantPage,
  ProvisionCompanyRequest,
  ProvisionCompanyResponse,
  TenantLifecycleResult,
} from '@/types/platform-auth.types';

/** Filters the Companies list accepts. All are applied server-side (G-4). */
export interface CompanyListParams {
  limit?: number;
  offset?: number;
  search?: string;
  status?: string;
  /**
   * Phase 12.3G — filters on the plan of the company's CANONICAL current
   * subscription (OD-1: `startedAt desc`), which is the same subscription the
   * Plan column shows. Never a "was ever on this plan" match.
   */
  planId?: string;
}

/**
 * Phase 12.2A (F-03) — `getById` returns `PlatformTenantDetail`.
 *
 * The two routes return genuinely different shapes (`TenantSummaryDto` vs
 * `TenantDetailDto`); typing both as the list item is what let the mismatch
 * survive unnoticed. The list stays guarded with `Array.isArray` per the
 * response-envelope convention in CLAUDE.md.
 *
 * ── Envelope reminder ──────────────────────────────────────────────────────
 * `platformApiClient` already unwraps `{ data, meta }`, so `r.data` IS the
 * payload. A second `.data` access here returns undefined — see CLAUDE.md.
 */
export const platformCompanyService = {
  /**
   * Phase 12.3E (G-4) — server-side paginated, returning
   * `{ items, total, limit, offset }`.
   */
  list: (params: CompanyListParams = {}) =>
    platformApiClient
      .get<PlatformTenantPage>('/platform/tenants', { params })
      .then((r): PlatformTenantPage => {
        const page = r.data;
        return {
          items: Array.isArray(page?.items) ? page.items : [],
          total: typeof page?.total === 'number' ? page.total : 0,
          limit: typeof page?.limit === 'number' ? page.limit : 0,
          offset: typeof page?.offset === 'number' ? page.offset : 0,
        };
      }),

  getById: (tenantId: string): Promise<PlatformTenantDetail> =>
    platformApiClient
      .get<PlatformTenantDetail>(`/platform/tenants/${tenantId}`)
      .then((r) => r.data),

  /**
   * Plans, for the Create form's plan select and the list's Plan filter.
   *
   * Requires `platform:subscriptions:view`, which is NOT implied by
   * `platform:companies:view` — `platform-support` holds the latter and not the
   * former. Callers must therefore treat a failure as "no plan filter
   * available" rather than as a page error; the Companies list still works
   * without it.
   */
  listPlans: (): Promise<PlatformPlan[]> =>
    platformApiClient
      .get<PlatformPlan[]>('/platform/plans')
      .then((r) => (Array.isArray(r.data) ? r.data : [])),

  /**
   * Phase 12.3F/G — provision a company.
   *
   * The request carries owner IDENTITY only. There is no password field, by
   * design (D-6): the owner is emailed an invitation and chooses their own
   * credential. The response reports whether that invitation was delivered.
   */
  provision: (payload: ProvisionCompanyRequest): Promise<ProvisionCompanyResponse> =>
    platformApiClient
      .post<ProvisionCompanyResponse>('/platform/tenants', payload)
      .then((r) => r.data),

  /** Phase 12.3F (G-6, OD-4) — reissue the owner invitation, invalidating the old one. */
  resendOwnerInvitation: (
    tenantId: string,
  ): Promise<{ delivered: boolean; expiresAt: string; deliveryError: string | null }> =>
    platformApiClient
      .post<{ delivered: boolean; expiresAt: string; deliveryError: string | null }>(
        `/platform/tenants/${tenantId}/owner-invitation/resend`,
      )
      .then((r) => r.data),

  // ── Lifecycle (Phase 12.3D, COMPANIES_DESIGN.md §6.1) ─────────────────────
  //
  // Four named actions, not one `PATCH { status }`. The backend owns the state
  // machine; the console cannot express an illegal transition because there is
  // no route for one.

  activate: (tenantId: string, reason?: string): Promise<TenantLifecycleResult> =>
    platformApiClient
      .post<TenantLifecycleResult>(`/platform/tenants/${tenantId}/activate`, { reason })
      .then((r) => r.data),

  suspend: (tenantId: string, reason: string): Promise<TenantLifecycleResult> =>
    platformApiClient
      .post<TenantLifecycleResult>(`/platform/tenants/${tenantId}/suspend`, { reason })
      .then((r) => r.data),

  reactivate: (tenantId: string, reason?: string): Promise<TenantLifecycleResult> =>
    platformApiClient
      .post<TenantLifecycleResult>(`/platform/tenants/${tenantId}/reactivate`, { reason })
      .then((r) => r.data),

  cancel: (tenantId: string, reason: string): Promise<TenantLifecycleResult> =>
    platformApiClient
      .post<TenantLifecycleResult>(`/platform/tenants/${tenantId}/cancel`, { reason })
      .then((r) => r.data),
};
